import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

// ========================================
// Types
// ========================================

export interface ExecutiveMetric {
  name: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export interface MetricsSummaryResponse {
  timeRange: string;
  timestamp: string;
  data: {
    revenue?: ExecutiveMetric;
    orders?: ExecutiveMetric;
    customers?: ExecutiveMetric;
    aov?: ExecutiveMetric;
    [key: string]: ExecutiveMetric | undefined;
  };
}

export interface TrendDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface TrendsResponse {
  metric: string;
  timeRange: string;
  granularity: string;
  data: TrendDataPoint[];
}

export interface QueryRequest {
  select?: string[];
  filter?: Record<string, any>;
  sortBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
  offset?: number;
  timeRange?: string;
}

export interface QueryResponse {
  tier: 'hot' | 'warm' | 'cold';
  data: any;
  analysis: {
    level: string;
    confidence: number;
    reasons: string[];
  };
  cache?: {
    key: string;
    ttl: number;
  };
}

export interface DimensionValue {
  value: string;
  label: string;
  count?: number;
}

export interface DimensionsResponse {
  dimension: string;
  values: DimensionValue[];
}

// ========================================
// API Hooks
// ========================================

/**
 * Hook: Get metrics summary from HOT tier
 * Use for dashboard KPI cards
 */
export function useMetricsSummary(
  timeRange: string = 'today',
  metrics?: string[],
  options?: UseQueryOptions<MetricsSummaryResponse>
) {
  return useQuery<MetricsSummaryResponse>({
    queryKey: ['executive', 'metrics', 'summary', timeRange, metrics],
    queryFn: async () => {
      const metricsParam = metrics?.join(',') || '';
      const url = `executive/metrics/summary?timeRange=${timeRange}${metricsParam ? `&metrics=${metricsParam}` : ''}`;
      return await apiClient.get<MetricsSummaryResponse>(url);
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    ...options,
  });
}

/**
 * Hook: Get time series trends from WARM tier
 * Use for trend charts
 */
export function useMetricTrends(
  metric: string,
  timeRange: string = '7d',
  granularity: string = 'day',
  options?: UseQueryOptions<TrendsResponse>
) {
  return useQuery<TrendsResponse>({
    queryKey: ['executive', 'metrics', 'trends', metric, timeRange, granularity],
    queryFn: async () => {
      const url = `executive/metrics/trends?metric=${metric}&timeRange=${timeRange}&granularity=${granularity}`;
      return await apiClient.get<TrendsResponse>(url);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!metric,
    ...options,
  });
}

/**
 * Hook: Execute smart query (auto-routed to appropriate tier)
 * Use for ad-hoc queries and data grids
 */
export function useExecutiveQuery(
  queryParams: QueryRequest,
  options?: UseQueryOptions<QueryResponse>
) {
  return useQuery<QueryResponse>({
    queryKey: ['executive', 'query', queryParams],
    queryFn: async () => {
      return await apiClient.post<QueryResponse>('executive/query', queryParams);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!queryParams.select || !!queryParams.filter,
    ...options,
  });
}

/**
 * Hook: Get dimension values for filters/autocomplete
 */
export function useDimensionValues(
  dimension: string,
  search?: string,
  limit: number = 50,
  options?: UseQueryOptions<DimensionsResponse>
) {
  return useQuery<DimensionsResponse>({
    queryKey: ['executive', 'dimensions', dimension, search, limit],
    queryFn: async () => {
      const url = `executive/dimensions/${dimension}?${search ? `search=${search}&` : ''}limit=${limit}`;
      return await apiClient.get<DimensionsResponse>(url);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!dimension,
    ...options,
  });
}

/**
 * Mutation: Refresh cache manually (admin)
 */
export function useRefreshCache() {
  return useMutation({
    mutationFn: async (params: { metrics?: string[]; timeRange?: string }) => {
      return await apiClient.post('executive/cache/refresh', params);
    },
  });
}

/**
 * Hook: Export data to CSV/JSON
 */
export function useExportData() {
  return useMutation({
    mutationFn: async (params: { format: 'json' | 'csv' | 'excel'; timeRange?: string; limit?: number }) => {
      const { format, timeRange = '30d', limit = 10000 } = params;
      const baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
      const url = `${baseUrl}/executive/data/export?format=${format}&timeRange=${timeRange}&limit=${limit}`;

      // For CSV/Excel, trigger download
      if (format === 'csv' || format === 'excel') {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${apiClient.getAccessToken()}`,
          },
        });

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `executive_export_${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { success: true };
      }

      // For JSON, return data
      return await apiClient.get(`executive/data/export?format=${format}&timeRange=${timeRange}&limit=${limit}`);
    },
  });
}
